import { useEffect, useState } from 'react';
import api from '../services/ApiService';

const AVATAR_COLORS = ['#2563eb','#16a34a','#9333ea','#dc2626','#0891b2','#d97706','#db2777'];

function avatarColor(name) {
  let n = 0;
  for (let i = 0; i < (name || '').length; i++) n += name.charCodeAt(i);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name[0].toUpperCase();
}

function fmtDate(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleDateString(); } catch { return null; }
}

const EMPTY_FORM = { name: '', number: '', email: '', company: '', notes: '', contact_type: 'personal' };

const TABS = [
  { id: 'personal', label: 'My Contacts',      icon: '👤' },
  { id: 'company',  label: 'Company Contacts',  icon: '🏢' },
];

export default function ContactsPanel({ onDial }) {
  const [contacts, setContacts]   = useState([]);
  const [search, setSearch]       = useState('');
  const [activeTab, setActiveTab] = useState('personal');

  const [showForm, setShowForm]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [loading, setLoading]     = useState(true);

  const load = async () => {
    try {
      const c = await api.getContacts();
      setContacts(c || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, contact_type: activeTab });
    setEditingId(null);
    setShowForm(false);
    setFormError('');
  };

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, contact_type: activeTab });
    setEditingId(null);
    setShowForm(true);
    setFormError('');
  };

  const openEdit = (c) => {
    setForm({
      name:         c.name         || '',
      number:       c.number       || '',
      email:        c.email        || '',
      company:      c.company      || '',
      notes:        c.notes        || '',
      contact_type: c.contact_type || 'personal',
    });
    setEditingId(c.id);
    setShowForm(true);
    setFormError('');
  };

  const saveContact = async () => {
    setFormError('');
    if (!form.name.trim()) { setFormError('Name is required.'); return; }

    const cleanNum = form.number.trim().replace(/[\s\-().]/g, '');
    if (!/^\+?[0-9]{7,15}$/.test(cleanNum)) {
      setFormError('Enter a valid phone number (7–15 digits, e.g. +971501234567).');
      return;
    }

    try {
      const payload = {
        name:         form.name.trim(),
        number:       cleanNum,
        email:        form.email.trim(),
        company:      form.company.trim(),
        notes:        form.notes.trim(),
        contact_type: form.contact_type,
      };

      if (editingId) {
        await api.updateContact(editingId, payload);
      } else {
        await api.addContact(payload);
      }
      resetForm();
      await load();
    } catch (e) {
      setFormError('Failed to save contact. Please try again.');
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try { await api.deleteContact(id); await load(); }
    catch { alert('Failed to delete.'); }
  };

  const dialContact = async (c) => {
    try { await api.updateContactLastDialed(c.id); } catch { /* silent */ }
    if (onDial) onDial(c.number);
  };

  // Filter to current tab
  const tabContacts = contacts
    .filter(c => (c.contact_type || 'personal') === activeTab)
    .filter(c => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [c.name, c.number, c.email, c.company, c.notes].some(v =>
        String(v || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

  const personalCount = contacts.filter(c => (c.contact_type || 'personal') === 'personal').length;
  const companyCount  = contacts.filter(c => c.contact_type === 'company').length;

  const renderCard = (c) => (
    <div key={c.id} style={{
      background: 'white', borderRadius: '14px', padding: '14px 16px',
      marginBottom: '10px', display: 'flex', alignItems: 'center',
      gap: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '50%',
        background: avatarColor(c.name), color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: '14px', flexShrink: 0,
      }}>
        {initials(c.name)}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '14px', color: '#111827' }}>{c.name}</div>
        <div style={{ fontSize: '13px', color: '#374151', marginTop: '1px' }}>{c.number}</div>
        {c.company && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '1px' }}>{c.company}</div>}
        {c.email   && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '1px' }}>{c.email}</div>}
        {c.last_dialed && (
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
            Last called {fmtDate(c.last_dialed)}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        <button
          onClick={() => dialContact(c)}
          style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
        >☎</button>
        <button
          onClick={() => openEdit(c)}
          style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', padding: '7px 12px', fontSize: '13px', cursor: 'pointer' }}
        >✎</button>
        <button
          onClick={() => deleteContact(c.id)}
          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', padding: '7px 12px', fontSize: '13px', cursor: 'pointer' }}
        >✕</button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <div className="admin-page-title">Contacts</div>
          <div className="admin-page-subtitle">{contacts.length} total contacts</div>
        </div>
        <button
          className={showForm && !editingId ? 'secondary-btn' : 'primary-btn'}
          onClick={() => { if (showForm && !editingId) { resetForm(); } else { openAdd(); } }}
        >
          {showForm && !editingId ? '✕ Cancel' : '+ Add Contact'}
        </button>
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="content-card" style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 700, marginBottom: '14px', fontSize: '15px' }}>
            {editingId ? 'Edit Contact' : 'New Contact'}
          </div>

          {/* Contact type selector */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setForm(f => ({ ...f, contact_type: t.id }))}
                style={{
                  padding: '8px 16px', borderRadius: '8px', border: '2px solid',
                  borderColor: form.contact_type === t.id ? '#2563eb' : '#e5e7eb',
                  background: form.contact_type === t.id ? '#eff6ff' : 'white',
                  color: form.contact_type === t.id ? '#2563eb' : '#6b7280',
                  fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <input className="admin-input" value={form.name}    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}    placeholder="Full name *" />
            <input className="admin-input" value={form.number}  onChange={e => setForm(f => ({ ...f, number: e.target.value }))}  placeholder="Phone number * e.g. +971501234567" />
            <input className="admin-input" value={form.email}   onChange={e => setForm(f => ({ ...f, email: e.target.value }))}   placeholder="Email" />
            <input className="admin-input" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Company" />
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Notes"
              rows={2}
              style={{ gridColumn: '1 / -1', padding: '11px 12px', border: '1px solid #d1d5db', borderRadius: '10px', resize: 'vertical', fontFamily: 'inherit', fontSize: '14px' }}
            />
          </div>

          {formError && (
            <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px' }}>
              {formError}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
            <button className="primary-btn" onClick={saveContact}>
              {editingId ? 'Update Contact' : 'Save Contact'}
            </button>
            <button className="secondary-btn" onClick={resetForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
        {TABS.map(t => {
          const count = t.id === 'personal' ? personalCount : companyCount;
          return (
            <button
              key={t.id}
              onClick={() => { setActiveTab(t.id); setSearch(''); }}
              style={{
                padding: '10px 18px', border: 'none', cursor: 'pointer',
                background: 'transparent', fontWeight: activeTab === t.id ? 700 : 500,
                fontSize: '14px',
                color: activeTab === t.id ? '#2563eb' : '#6b7280',
                borderBottom: activeTab === t.id ? '2px solid #2563eb' : '2px solid transparent',
                marginBottom: '-2px',
              }}
            >
              {t.icon} {t.label}
              <span style={{
                marginLeft: '6px', fontSize: '11px', fontWeight: 600,
                background: activeTab === t.id ? '#dbeafe' : '#f3f4f6',
                color: activeTab === t.id ? '#2563eb' : '#6b7280',
                padding: '1px 7px', borderRadius: '99px',
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <input
        className="admin-input"
        style={{ width: '100%', marginBottom: '16px', boxSizing: 'border-box' }}
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={`Search ${activeTab === 'personal' ? 'personal' : 'company'} contacts…`}
      />

      {/* List */}
      {loading && <p className="admin-muted">Loading…</p>}

      {!loading && tabContacts.length === 0 && (
        <div className="content-card" style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>
            {activeTab === 'personal' ? '👤' : '🏢'}
          </div>
          <p className="admin-muted">
            {search
              ? 'No contacts match your search.'
              : `No ${activeTab === 'personal' ? 'personal' : 'company'} contacts yet. Click "+ Add Contact" to create one.`}
          </p>
        </div>
      )}

      {!loading && tabContacts.map(renderCard)}
    </div>
  );
}
